import type { ComponentDefinition, Terminal } from "../types";

function contact(id: string, label: string, maxAmp: number, side: Terminal["side"], offset: number): Terminal {
  return { id, label, maxAmp, side, offset, polarity: "neutral", direction: "bidi" };
}

/** These are contact diagrams. Coil voltage, timing, spring return and automatic
 * operation are not simulated: each scenario selects its contact state. */
export const CONTROL_CIRCUITS: ComponentDefinition[] = [
  {
    // Manufacturer specifies three contacts, 15 A at 12 V DC.
    // https://www.bluesea.com/products/4152/WeatherDeck_Toggle_Switch_SPDT_-_ON-OFF-ON
    id: "dist-spdt-center-off",
    name: "Blue Sea 4152 SPDT Center-Off Switch",
    shortName: "SPDT ON–OFF–ON",
    subtitle: "Three-terminal changeover switch, 15A at 12V DC",
    category: "distribution", manufacturer: "Blue Sea Systems", model: "4152",
    image: "dist-spdt-center-off.webp",
    compatibleVoltages: [12], width: 180, height: 120,
    terminals: [contact("com", "COM", 15, "left", 0.5), contact("a", "A", 15, "right", 0.25), contact("b", "B", 15, "right", 0.75)],
    simulateStates: {
      default: "a",
      options: [
        { id: "a", label: "A", intraLinks: [["com", "a"]] },
        { id: "off", label: "Center off", intraLinks: [] },
        { id: "b", label: "B", intraLinks: [["com", "b"]] },
      ],
    },
  },
  {
    // 4155 is 5 A / 30 V DC, NOT the 15 A rating of the SPDT 4152.
    // https://www.bluesea.com/products/4155/WeatherDeck_Toggle_Switch_DPDT_-_ON-OFF-ON
    id: "dist-dpdt-center-off",
    name: "Blue Sea 4155 DPDT Center-Off Switch",
    shortName: "DPDT ON–OFF–ON",
    subtitle: "Two independent changeover poles, 5A / 30V DC",
    category: "distribution", manufacturer: "Blue Sea Systems", model: "4155",
    image: "dist-dpdt-center-off.webp",
    compatibleVoltages: [12, 24], width: 200, height: 170,
    terminals: [
      contact("com1", "COM 1", 5, "left", 0.3), contact("com2", "COM 2", 5, "left", 0.7),
      contact("a1", "A 1", 5, "right", 0.12), contact("a2", "A 2", 5, "right", 0.36),
      contact("b1", "B 1", 5, "right", 0.64), contact("b2", "B 2", 5, "right", 0.88),
    ],
    simulateStates: {
      default: "a",
      options: [
        { id: "a", label: "A", intraLinks: [["com1", "a1"], ["com2", "a2"]] },
        { id: "off", label: "Center off", intraLinks: [] },
        { id: "b", label: "B", intraLinks: [["com1", "b1"], ["com2", "b2"]] },
      ],
    },
  },
  {
    // The manufacturer's parentheses identify a momentary ON position.
    // https://www.bluesea.com/products/compare/4150/4151/4152/4153/4154/4155
    id: "dist-momentary-spst",
    name: "Blue Sea 4151 Momentary SPST Switch",
    shortName: "Momentary (ON)–OFF",
    subtitle: "Normally open push control, 15A at 12V DC",
    category: "distribution", manufacturer: "Blue Sea Systems", model: "4151",
    image: "dist-momentary-spst.webp",
    compatibleVoltages: [12], width: 170, height: 90,
    terminals: [contact("com", "COM", 15, "left", 0.5), contact("no", "NO", 15, "right", 0.5)],
    simulateStates: {
      default: "released",
      options: [
        { id: "released", label: "Released", intraLinks: [] },
        { id: "held", label: "Held (manual scenario)", intraLinks: [["com", "no"]] },
      ],
    },
  },
  {
    // HELLA 3080 / 4RD 933 332-397: 30→87 30 A; 30→87a 20 A;
    // coil draw 140 mA. Do not apply the NO rating to the NC contact.
    // https://www.hella.com/salesapp/production/5100/HELLA_Universal_Lighting_Electrical_Products_Catalogue_EN.pdf (p271)
    id: "dist-relay-spdt-12v",
    image: "dist-relay-spdt-12v.webp",
    name: "HELLA 3080 12V Five-Pin Changeover Relay",
    shortName: "5-pin relay · NO / NC",
    subtitle: "30/85/86/87/87a · 30A NO / 20A NC",
    category: "distribution", manufacturer: "HELLA", model: "3080 / 4RD 933 332-397",
    compatibleVoltages: [12], width: 210, height: 150,
    terminals: [
      contact("30", "30 COM", 30, "left", 0.4),
      contact("87", "87 NO", 30, "right", 0.25),
      contact("87a", "87a NC", 20, "right", 0.7),
      // Coil pins are deliberately separate from the power graph. The
      // published 140 mA operating draw is not a terminal ampacity rating.
      { id: "86", label: "86 COIL +", polarity: "signal", direction: "input", maxAmp: 0, side: "bottom", offset: 0.3 },
      { id: "85", label: "85 COIL −", polarity: "signal", direction: "input", maxAmp: 0, side: "bottom", offset: 0.7 },
    ],
    simulateStates: {
      default: "rest",
      options: [
        { id: "rest", label: "Rest · 30→87a (manual)", intraLinks: [["30", "87a"]] },
        { id: "energized", label: "Energized · 30→87 (manual)", intraLinks: [["30", "87"]] },
      ],
    },
  },
];

export const CONTROL_CONTACT_GROUPS: Readonly<Record<string, readonly (readonly string[])[]>> = {
  "dist-spdt-center-off": [["com", "a", "b"]],
  "dist-dpdt-center-off": [["com1", "a1", "b1"], ["com2", "a2", "b2"]],
  "dist-momentary-spst": [["com", "no"]],
  "dist-relay-spdt-12v": [["30", "87", "87a"]],
};
